/**
 * Bounded fetch + auth-cookie helpers for the Supabase clients.
 *
 * Supabase auth calls (getClaims/getUser → GoTrue via Kong) are made on the
 * request hot path (middleware + SSR). With no AbortSignal, a stalled GoTrue
 * (e.g. during a token-refresh storm) blocks the request until Cloudflare's
 * 100s cap → 524. Wrapping the client's `global.fetch` with a timeout turns
 * that into a fast failure (render as logged-out) instead of a hang.
 */

/** Max time any Supabase network call may take before it's aborted. */
export const SUPABASE_FETCH_TIMEOUT_MS = 6000;

/** A `fetch` that aborts after {@link SUPABASE_FETCH_TIMEOUT_MS}. Works in the
 * Edge (middleware) and Node (SSR) runtimes. */
export const timeoutFetch: typeof fetch = (input, init) => {
  const timeout = AbortSignal.timeout(SUPABASE_FETCH_TIMEOUT_MS);
  const signal =
    init?.signal && typeof AbortSignal.any === 'function'
      ? AbortSignal.any([init.signal, timeout])
      : timeout;
  return fetch(input, { ...init, signal });
};

/**
 * Which login a request belongs to. The admin and the storefront share a host,
 * so they used to share one auth cookie: signing into the admin signed you
 * into the storefront as that account, and signing out of either ended both.
 * Each area now has its own cookie.
 */
export type SessionArea = 'admin' | 'store';

/** `/admin…` and `/api/admin…` are the admin; everything else is the store. */
export function sessionArea(pathname: string | null | undefined): SessionArea {
  if (!pathname) return 'store';
  return /^\/(api\/)?admin(\/|$)/.test(pathname) ? 'admin' : 'store';
}

/** The Supabase auth cookie name for a host and area. The storefront keeps
 * `sb-<subdomain>-auth-token` (customers' existing sessions stay valid); the
 * admin uses `sb-<subdomain>-admin-auth-token`. Every client must agree. */
export function authCookieKey(host: string, area: SessionArea = 'store'): string {
  const sub = host.split(':')[0].split('.')[0];
  return area === 'admin' ? `sb-${sub}-admin-auth-token` : `sb-${sub}-auth-token`;
}

/** True when an auth cookie (possibly chunked `.0`/`.1`) is present for `host`.
 * Lets the hot path skip GoTrue entirely for anonymous visitors. */
export function hasAuthCookie(
  cookieNames: string[],
  host: string,
  area: SessionArea = 'store',
): boolean {
  const key = authCookieKey(host, area);
  return cookieNames.some((n) => n === key || n.startsWith(key + '.'));
}
